// HU-26 · HU-48 · HU-52 · Los documentos del expediente POR GRAVEDAD (`ui/MG-ILE-54.md`
// «Documentos del expediente», `ui/MG-ILE-37.md` y `ui/MG-ILE-912.md`).
//
// LA PANTALLA PINTA LO QUE EL SERVIDOR DICE (§8.1 R1). `expediente.filas[]` trae una fila por
// documento que le toca al caso —un no grave no lista Formato Amarillo, carta ni cargo— con su
// `estado` (`generado` · `pendiente` · `fallido` · `hay_que_aportarlo`), su `texto_del_estado`
// y las acciones que ESTE actor puede hacer (`descargar`, `reintentar`). Aquí no se decide qué
// documento falta ni si se puede reintentar.
//
// UNA FILA DE LA TABLA POR VERSIÓN ARCHIVADA (HU-26-C6, HU-48-C7: «la anterior se conserva»),
// con el texto que arma el servidor («Informe FAR-03 · v2 · 07/10/2026 · Vigente»). Un encargo
// abierto —pendiente o fallido— suma su propia fila con el estado y, si el servidor lo ofrece,
// «Reintentar». Lo que todavía no tiene ni versión ni encargo es un FALTANTE y vive en el aviso
// «El expediente todavía no está completo», no aquí (dos cosas distintas, `MG-ILE-912.md` v3).
//
// Todas las filas de un mismo tipo comparten testid (`expediente-documento-far03`, …): la
// prueba las distingue por su texto («v1», «v2»), nunca por posición.

import { AlertTriangle, Clock, Eye, RotateCcw, ShieldAlert } from 'lucide-react';
import { Button, DataTable, Mono, StatusBadge } from './ui';
import type { ColumnaDeTabla, FilaDeTabla, Icono } from './ui';
import type { FilaDelExpediente, VersionDeLaFila } from '../api/expedientes';
import { tonoDelDocumento } from '../lib/etiquetas';
import { comoFecha } from '../lib/fechas';

/** `formato_amarillo` → `formato-amarillo`: el tipo en kebab-case para el testid de la spec. */
export const tipoEnKebab = (tipo: string): string => tipo.split('_').join('-');

/**
 * CC-02 (B9): la fila del documento se identifica por su tipo y, si es de una carta, por la
 * autoridad de esa carta (`carta·SANIPES`). Con una sola carta o en los documentos del caso,
 * el tipo solo, como siempre.
 */
export const claveDeLaFila = (fila: FilaDelExpediente): string =>
  (fila.autoridad_codigo ? `${fila.tipo}·${fila.autoridad_codigo}` : fila.tipo);

const ICONO_DEL_ESTADO: Record<string, Icono> = { pendiente: Clock, fallido: AlertTriangle, hay_que_aportarlo: Clock };

/** Una fila de la tabla: una versión archivada o el encargo abierto de su tipo. */
type FilaDeDocumento = FilaDeTabla & {
  id: string;
  fila: FilaDelExpediente;
  version: VersionDeLaFila | null;
};

/** Lo que la tabla sabe de una versión por el `documentos[]` vigente: si lleva dato de personas (HU-52-C2). */
export type DatoDePersonasPorDocumento = Record<string, boolean>;

/** Las filas de la tabla a partir de `filas[]`: las versiones y, si lo hay, el encargo abierto. */
export function filasDeLaTabla(filas: FilaDelExpediente[]): FilaDeDocumento[] {
  const salida: FilaDeDocumento[] = [];
  for (const fila of filas) {
    for (const version of fila.versiones) salida.push({ id: version.documento_id, fila, version });
    const encargoAbierto = fila.estado !== 'generado' && fila.estado !== 'hay_que_aportarlo'
      && (fila.version_del_encargo !== null || fila.versiones.length > 0);
    // CC-02 (B9): con varias cartas hay un encargo por autoridad del mismo tipo y versión.
    if (encargoAbierto) salida.push({ id: `${claveDeLaFila(fila)}-encargo-${fila.version_del_encargo ?? 'sin'}`, fila, version: null });
  }
  return salida;
}

/** Columna «Documento»: el texto de la versión con su huella, o la etiqueta del encargo abierto. */
function celdaDelDocumento(fila: FilaDelExpediente, version: VersionDeLaFila | null, prefijo: string) {
  if (!version) {
    return (
      <p className="font-semibold text-ink text-body">
        {fila.etiqueta}{fila.version_del_encargo !== null && <> <Mono>v{fila.version_del_encargo}</Mono></>}
      </p>
    );
  }
  return (
    <div>
      <p className="font-semibold text-ink text-body">{version.texto}</p>
      {/* HU-52-C3: la carta dice cuándo se envió (el día que registra el trámite). */}
      {fila.tipo === 'carta' && fila.enviada_en && version.es_la_vigente && (
        <p className="text-meta text-ink-soft">enviada {comoFecha(String(fila.enviada_en).slice(0, 10))}</p>
      )}
      {/* ADR-003 / RN-51: la huella prueba que el PDF archivado es el emitido. */}
      <span title={version.huella_sha256} data-testid={`${prefijo}-huella`}>
        <Mono className="text-meta text-ink-soft">{version.huella_sha256.slice(0, 16)}…</Mono>
      </span>
    </div>
  );
}

/** Columna «Estado»: la insignia del encargo abierto o el texto del servidor en la vigente. */
function celdaDelEstado(fila: FilaDelExpediente, version: VersionDeLaFila | null, prefijo: string) {
  if (!version) {
    // Pendiente (reloj) o fallido (alerta): texto del servidor, nunca solo color (RNF-11).
    return (
      <StatusBadge tone={tonoDelDocumento(fila.estado)} icon={ICONO_DEL_ESTADO[fila.estado] ?? Clock} data-testid={`${prefijo}-estado`}>
        {fila.texto_del_estado}
      </StatusBadge>
    );
  }
  // Generado: «sin insignia, se ve la versión» (`MG-ILE-912.md`); el texto del servidor
  // solo en la vigente, que es la que el estado describe.
  return version.es_la_vigente && fila.estado === 'generado'
    ? <span className="text-meta text-ink-soft" data-testid={`${prefijo}-estado`}>{fila.texto_del_estado}</span>
    : null;
}

/** Columna del dato de personas (HU-52-C2): la marca L2 si la versión lo lleva o está reservada. */
function celdaDelDatoDePersonas(fila: FilaDelExpediente, version: VersionDeLaFila | null, prefijo: string, datoDePersonas: DatoDePersonasPorDocumento) {
  if (!version) return null;
  const conDatoDePersonas = Boolean(datoDePersonas[version.documento_id]);
  if (!conDatoDePersonas && !fila.reservado_l2) return null;
  return (
    <div data-testid={`${prefijo}-l2`}>
      <StatusBadge tone="danger" icon={ShieldAlert}>Dato de personas</StatusBadge>
      <p className="text-meta text-ink-soft mt-1 max-w-[13.75rem]">
        {fila.texto_reservado ?? 'Contiene datos de una persona (L2). Abrirlo exige permiso y queda registrado en la auditoría.'}
      </p>
    </div>
  );
}

/** Lo que la columna de acciones necesita de la tabla: qué está en vuelo y a quién avisar. */
type AccionesDeLaTabla = {
  abriendo: string | null;
  reintentando: string | null;
  onVerPdf: (version: VersionDeLaFila, fila: FilaDelExpediente) => void;
  onReintentar: (fila: FilaDelExpediente) => void;
};

/** Columna de acciones: «Ver PDF» en una versión, «Reintentar» en el encargo si el servidor lo ofrece. */
function celdaDeAcciones(fila: FilaDelExpediente, version: VersionDeLaFila | null, prefijo: string, acciones: AccionesDeLaTabla) {
  const { abriendo, reintentando, onVerPdf, onReintentar } = acciones;
  if (version) {
    // La copia L2 conserva «descargar» aunque esté reservada: el intento denegado se
    // audita (HU-26-C8). El servidor decide si la ofrece.
    if (!version.acciones_permitidas.includes('descargar')) return null;
    return (
      <Button size="sm" variant="secondary" icon={Eye} data-testid={`${prefijo}-descargar`}
        aria-label={`Ver PDF de ${version.texto}`}
        disabled={abriendo === version.documento_id} onClick={() => onVerPdf(version, fila)}>
        {abriendo === version.documento_id ? 'Abriendo…' : 'Ver PDF'}
      </Button>
    );
  }
  if (!fila.acciones_permitidas.includes('reintentar') || fila.version_del_encargo === null) return null;
  return (
    <Button size="sm" variant="secondary" icon={RotateCcw} data-testid={`${prefijo}-reintentar`}
      aria-label={`Reintentar ${fila.etiqueta} v${fila.version_del_encargo}`}
      disabled={reintentando === claveDeLaFila(fila)} onClick={() => onReintentar(fila)}>
      {reintentando === claveDeLaFila(fila) ? 'Reintentando…' : 'Reintentar'}
    </Button>
  );
}

export default function DocumentosDelExpediente({ filas, datoDePersonas, abriendo, reintentando, onVerPdf, onReintentar }: {
  filas: FilaDelExpediente[];
  /** `documento_id` → `contiene_pii`, del `documentos[]` del mismo expediente. */
  datoDePersonas: DatoDePersonasPorDocumento;
  /** El `documento_id` que se está abriendo, para apagar su botón. */
  abriendo: string | null;
  /** La fila (`claveDeLaFila`: tipo y, por carta, autoridad) cuyo reintento está en vuelo. */
  reintentando: string | null;
  onVerPdf: (version: VersionDeLaFila, fila: FilaDelExpediente) => void;
  onReintentar: (fila: FilaDelExpediente) => void;
}) {
  const columnas: ColumnaDeTabla[] = [
    { key: 'documento', label: 'Documento' },
    { key: 'estado', label: 'Estado' },
    { key: 'pii', label: <span className="sr-only">Datos personales</span> },
    { key: 'acciones', label: '' },
  ];
  const filasDeTabla = filasDeLaTabla(filas);

  const celda = (r: FilaDeTabla, { key }: ColumnaDeTabla) => {
    const { fila, version } = r as FilaDeDocumento;
    const prefijo = `expediente-documento-${tipoEnKebab(fila.tipo)}`;
    switch (key) {
      case 'documento': return celdaDelDocumento(fila, version, prefijo);
      case 'estado': return celdaDelEstado(fila, version, prefijo);
      case 'pii': return celdaDelDatoDePersonas(fila, version, prefijo, datoDePersonas);
      case 'acciones': return celdaDeAcciones(fila, version, prefijo, { abriendo, reintentando, onVerPdf, onReintentar });
      default: return null;
    }
  };

  return (
    <DataTable columns={columnas} rows={filasDeTabla} renderCell={celda} sinMarco data-testid="expediente-documentos"
      regionDesplazable="Documentos del expediente"
      testidDeFila={(r) => `expediente-documento-${tipoEnKebab((r as FilaDeDocumento).fila.tipo)}`} />
  );
}
